import {
  ChangeDetectionStrategy,
  Component,
  signal,
  OnInit,
  inject,
  DestroyRef,
} from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ButtonComponent } from '../../../../ui-components/lib/button/button.component';
import { InputComponent } from '../../../../ui-components/lib/input/input.component';
import { ValidationMessageComponent } from '../../../../ui-components/lib/validation-message/validation-message.component';
import { ToasterService } from '../../../../ui-components/services/toast/toast.service';
import {
  LockIconComponent,
  ViewIconComponent,
  EyeHiddenIconComponent,
} from '@kuiper/icons';
import { Router } from '@angular/router';
import {
  passwordValidation,
  regexVariables,
} from '../../../common/constants/regex.constant';
import { AuthService } from '../../../common/services/auth/auth.service';
import { OverlayLoaderComponent } from '../../../common/components/overlay-loader/overlay-loader.component';
import { TenantService } from '../../../common/services/tenant/tenant.service';
import { AUTH_ROUTES } from '../../../common/constants/auth.routing.constants';
import { SystemDefaultColorDirective } from '../../../common/directives/system-default-color/system-default-color.directive';
import { LogoutHelperService } from '../../../common/helpers/logout-helper/logout-helper.service';
import { finalize } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'lib-reset-password',
  imports: [
    OverlayLoaderComponent,
    TranslocoPipe,
    ReactiveFormsModule,
    InputComponent,
    ValidationMessageComponent,
    ButtonComponent,
    LockIconComponent,
    ViewIconComponent,
    EyeHiddenIconComponent,
  ],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  hostDirectives: [SystemDefaultColorDirective],
})
export class ResetPasswordComponent implements OnInit {
  authService = inject(AuthService);
  router = inject(Router);
  toasterService = inject(ToasterService);
  tenantService = inject(TenantService);
  readonly logoutHelper = inject(LogoutHelperService);
  readonly destroyRef = inject(DestroyRef);

  isLoading = signal<boolean>(false);
  isSuccessScreen = signal<boolean>(false);
  userData = signal<any>(null);
  createPasswordForm = signal<FormGroup>(new FormGroup({}));

  passwordValidationStatus = {
    length: false,
    upperCase: false,
    lowerCase: false,
    number: false,
    specialChar: false,
    touched: false,
  };
  showCurrentPassword = false;
  showPassword = false;
  showConfirmationPassword = false;
  isDisabled = false;

  constructor() {
    this.initCreatePasswordForm();
  }

  ngOnInit() {
    this.userData.set(JSON.parse(localStorage.getItem('auth_data') || '{}'));
  }

  initCreatePasswordForm() {
    this.createPasswordForm.set(
      new FormGroup(
        {
          current_password: new FormControl('', [Validators.required]),
          new_password: new FormControl('', [
            Validators.required,
            Validators.pattern(regexVariables.password),
          ]),

          new_password_confirmation: new FormControl('', Validators.required),
        },
        {
          validators: this.authService.matchingPasswords(
            'new_password',
            'new_password_confirmation'
          ),
        }
      )
    );

    const passwordControl = this.createPasswordForm().get('new_password');

    passwordControl?.valueChanges.subscribe(password => {
      this.validatePassword(password);
      this.passwordValidationStatus.touched = passwordControl.touched;
    });
  }

  validatePassword(password: string) {
    this.passwordValidationStatus = {
      ...this.passwordValidationStatus,
      length: password.length >= passwordValidation.minLength,
      upperCase: passwordValidation.hasUpperCase.test(password),
      lowerCase: passwordValidation.hasLowerCase.test(password),
      number: passwordValidation.hasNumber.test(password),
      specialChar: passwordValidation.hasSpecialChar.test(password),
    };
  }

  resetPassword() {
    this.isDisabled = true;
    this.authService
      .changePassword(this.createPasswordForm().value)
      .pipe(
        finalize(() => {
          this.isDisabled = false;
          this.logoutHelper.handleLogout();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        error: (error: any) => {
          this.toasterService.add(error?.message, '', '', 'error', true, true);
        },
      });
  }
}
